import React, { useState, useRef, useCallback } from 'react';
import { bool, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { OutsideClickHandler } from '../../../components';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import FilterForm from '../FilterForm/FilterForm';

import css from './FilterPopup.module.css';

const KEY_CODE_ESCAPE = 27;

function FilterPopup({
  rootClassName = null,
  className = null,
  popupClassName = null,
  id,
  onSubmit,
  initialValues = null,
  keepDirtyOnReinitialize = false,
  contentPlacementOffset = 0,
  label = null,
  labelMaxWidth = null,
  isSelected,
  children,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef(null);
  const filterContentRef = useRef(null);

  const handleSubmit = useCallback(
    values => {
      setIsOpen(false);
      onSubmit(values);
    },
    [onSubmit]
  );

  const handleClear = useCallback(() => {
    setIsOpen(false);
    onSubmit(null);
  }, [onSubmit]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    onSubmit(initialValues);
  }, [onSubmit, initialValues]);

  const handleBlur = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleKeyDown = useCallback(e => {
    if (e.keyCode === KEY_CODE_ESCAPE) {
      setIsOpen(false);
    }
  }, []);

  const toggleOpen = useCallback(enforcedState => {
    if (typeof enforcedState === 'boolean') {
      setIsOpen(enforcedState);
    } else {
      setIsOpen(prev => !prev);
    }
  }, []);

  const positionStyleForContent = useCallback(() => {
    const filter = filterRef.current;
    const filterContent = filterContentRef.current;
    if (filter && filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - filter.getBoundingClientRect().right;
      const labelWidth = filter.offsetWidth;
      const contentWidth = filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }, [contentPlacementOffset]);

  const classes = classNames(rootClassName || css.root, className);
  const popupClasses = classNames(css.popup, { [css.isOpen]: isOpen });
  const popupSizeClasses = popupClassName || css.popupSize;
  const contentStyle = positionStyleForContent();

  return (
    <OutsideClickHandler onOutsideClick={handleBlur}>
      <div className={classes} onKeyDown={handleKeyDown} ref={filterRef}>
        <PopupOpenerButton
          isSelected={isSelected}
          labelMaxWidth={labelMaxWidth}
          toggleOpen={toggleOpen}
        >
          {label}
        </PopupOpenerButton>
        <div id={id} className={popupClasses} ref={filterContentRef} style={contentStyle}>
          {isOpen ? (
            <FilterForm
              id={`${id}.form`}
              paddingClasses={popupSizeClasses}
              showAsPopup
              contentPlacementOffset={contentPlacementOffset}
              initialValues={initialValues}
              keepDirtyOnReinitialize={keepDirtyOnReinitialize}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onClear={handleClear}
            >
              {children}
            </FilterForm>
          ) : null}
        </div>
      </div>
    </OutsideClickHandler>
  );
}



FilterPopup.propTypes = {
  rootClassName: string,
  className: string,
  popupClassName: string,
  id: string.isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
  keepDirtyOnReinitialize: bool,
  contentPlacementOffset: number,
  label: string.isRequired,
  labelMaxWidth: number,
  isSelected: bool.isRequired,
  children: node.isRequired,
};

export default FilterPopup;
